import { Button } from '@/components/ui/button';
import { TaskSetup, TaskStageSkeleton } from '@/features/contribution-kit';
import { Icon } from '@/lib/icons';
import type { LabelImagesCopy } from '../label-images-copy';

/** Before an "Images" session: what it is, its size, its rules and the start button. */
export function LabelImagesSetup({
  copy,
  imageCount,
  onStart,
  onBack,
}: {
  copy: LabelImagesCopy;
  imageCount: number;
  onStart: () => void;
  onBack: () => void;
}) {
  return (
    <TaskSetup
      task="images"
      title={copy.title}
      description={copy.description}
      stats={[
        { value: imageCount, label: copy.statImages },
        { value: '~3', label: copy.statMinutes },
      ]}
      instructions={[copy.instruction1, copy.instruction2, copy.instruction3, copy.instruction4]}
      startLabel={copy.start}
      onStart={onStart}
      onBack={onBack}
    >
      <p className="text-sm leading-6 text-muted-foreground">{copy.setupNote}</p>
    </TaskSetup>
  );
}

/** While the session is being created. */
export function LabelImagesLoading({ copy }: { copy: LabelImagesCopy }) {
  return (
    <div data-testid="label-images-loading" aria-label={copy.loading}>
      <TaskStageSkeleton />
    </div>
  );
}

/** The session could not be created: the error and a way back. */
export function LabelImagesError({ copy, message, onBack }: { copy: LabelImagesCopy; message: string | null; onBack: () => void }) {
  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center px-4 py-14 text-center sm:px-6" role="alert">
      <span className="grid size-12 place-items-center rounded-full bg-danger/10 text-danger">
        <Icon name="alertTriangle" className="size-6" />
      </span>
      <h1 className="mt-5 text-xl font-semibold tracking-tight text-foreground">{copy.errorTitle}</h1>
      {message ? <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{message}</p> : null}
      <Button variant="outline" className="mt-8 h-12 w-full sm:w-auto sm:min-w-44" onClick={onBack}>
        {copy.back}
      </Button>
    </div>
  );
}
